import { BEARER_TOKEN_KEY } from "./auth-client";
import { merkeSpur } from "../components/tickets/ticketSpuren";

const defaultBase = import.meta.env.DEV ? 'http://localhost:3500/api' : '/api';
const rawBase = import.meta.env.VITE_API_BASE || defaultBase;
export const API_BASE = rawBase.replace(/\/+$/, '');

const buildBaseUrl = () => {
  if (/^https?:\/\//i.test(API_BASE)) {
    return API_BASE;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const basePath = API_BASE.startsWith('/') ? API_BASE : `/${API_BASE}`;
  return `${origin}${basePath}`;
};

export const apiUrl = (path: string, params?: Record<string, string | number | boolean>) => {
  const cleanPath = path.replace(/^\/+/, '');
  const url = new URL(`${buildBaseUrl()}/${cleanPath}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)));
  return url.toString();
};

// Monkey-patch global fetch so every API call carries the Bearer token (when present).
// Cookies are no longer used — better-auth's bearer plugin captures the token on signIn
// (see auth-client.ts) and the backend AuthzGuard accepts it via Authorization: Bearer.
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;

  // Fehlgeschlagene API-Aufrufe (Status ≥ 400 oder Netzwerkfehler) landen als
  // technische Spur fürs Ticket — bewusst ohne Request-/Response-Inhalte.
  const mitSpur = (p: Promise<Response>, istApi: boolean, url: string, methode: string): Promise<Response> => {
    if (!istApi) return p;
    const pfad = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
    return p.then(
      (res) => { if (res.status >= 400) merkeSpur("api", `${methode.toUpperCase()} ${pfad} → ${res.status}`); return res; },
      (err) => {
        // T-26: Ein Abbruch (AbortController, z. B. überholte Tippsuche) ist kein
        // Netzwerkfehler — er erschien im Ticket als „zuweisen-suche →
        // Netzwerkfehler" und führte bei der Analyse auf eine falsche Fährte.
        if ((err as { name?: string } | null)?.name !== "AbortError") {
          merkeSpur("api", `${methode.toUpperCase()} ${pfad} → Netzwerkfehler`);
        }
        throw err;
      },
    );
  };

  window.fetch = async function (resource: RequestInfo | URL, config?: RequestInit) {
    let urlStr = '';
    if (typeof resource === 'string') {
      urlStr = resource;
    } else if (resource instanceof URL) {
      urlStr = resource.toString();
    } else if (resource instanceof Request) {
      urlStr = resource.url;
    }

    const istApi = urlStr.startsWith(API_BASE) || urlStr.startsWith(buildBaseUrl());

    if (istApi) {
      const token = localStorage.getItem(BEARER_TOKEN_KEY);
      if (token) {
        const headers = new Headers(
          config?.headers || (resource instanceof Request ? resource.headers : undefined),
        );
        if (!headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        config = { ...(config || {}), headers };
        if (resource instanceof Request) {
          return mitSpur(originalFetch(new Request(resource, config)), istApi, urlStr, config?.method ?? (resource instanceof Request ? resource.method : "GET"));
        }
      }
    }
    return mitSpur(originalFetch(resource, config), istApi, urlStr, config?.method ?? (resource instanceof Request ? resource.method : "GET"));
  };
}
